import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { Loading } from 'src/components/pages/Loading';
import { useGetReportByIdQuery } from 'src/graphql';
import I18next from '../../../../i18n';
import { ReportCard } from '../../Common/ReportCard/ReportCard';
import { ReportingMessageSlideIn } from '../ReportingMessageSlideIn';
import {
  ReportContainer,
  StyledEventsTitle,
  StyledLink,
  StyledTab,
  StyledTabContainer,
} from './ReportingReport.styles';
import { ReportingReportView } from './ReportingReportView';

export const ReportingReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { reportId } = useParams<{ reportId: string }>();
  const [messagesOpen, setMessagesOpen] = useState<boolean>(false);
  const { data, isLoading } = useGetReportByIdQuery(
    {
      reportId: reportId ?? '',
    },
    {
      enabled: !!reportId,
    },
  );

  const report = data?.getReportById;
  const events = useMemo(() => report?.evidence?.events || [], [report]);
  const [activeTab, setActiveTab] = useState(events[0]?.id);

  useEffect(() => {
    if (!activeTab && events.length > 0) {
      setActiveTab(events[0].id);
    }
  }, [events, activeTab]);

  if (isLoading || !report || !reportId) {
    return <Loading />;
  }

  const direction = I18next.dir ? I18next.dir() : 'ltr';

  const handleAddEvidenceSelected = () => {
    navigate(`add-evidence`);
  };

  return (
    <ReportContainer>
      <ReportingMessageSlideIn
        open={messagesOpen}
        setOpen={setMessagesOpen}
        reportDetails={{
          internalId: reportId,
          reportReadableId: report.reportReadableId,
          submittedAt: report.submittedAt,
          status: report.status,
          category: report.reportedCategoryLocale?.name || '',
        }}
      />
      <StyledLink to="/reporting/reports" data-testid="report-back-button">
        <StyledIcon
          className={`uil uil-angle-${direction === 'ltr' ? 'left' : 'right'}`}
          size="28px"
        />
        {t('public.PublicReport.backToReports')}
      </StyledLink>
      <ReportCard
        report={report}
        version={'report-view'}
        onAddEvidenceSelected={() => handleAddEvidenceSelected()}
        onMessageCaseManagerSelected={() => setMessagesOpen(true)}
      />
      {events.length > 1 && (
        <div>
          <StyledEventsTitle>Events ({events.length})</StyledEventsTitle>

          <StyledTabContainer>
            {events.map((event, index) => {
              const isActive = activeTab === event.id;
              return (
                <StyledTab
                  key={event.id}
                  $isActive={isActive}
                  $isLast={index === events.length - 1}
                  data-testid={`report-event-tab-${index + 1}`}
                  onClick={() => setActiveTab(event.id)}
                >
                  Event {index + 1}
                </StyledTab>
              );
            })}
          </StyledTabContainer>
        </div>
      )}
      {events.length > 0 && (
        <ReportingReportView report={report} activeTab={activeTab} />
      )}
    </ReportContainer>
  );
};
